import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog as MuiDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { FORM_ERROR } from "final-form";
import { useState } from "react";

import { Form, type SelectItem, SubmitButton, type SwitchValue, type TextFieldValue } from "../../atoms/Form";
import {
  BubbleSelect,
  type BubbleSelectProps,
  Description,
  FIELD_NAME_BUBBLE_SELECT,
  FIELD_NAME_DESCRIPTION,
  FIELD_NAME_IS_AUTOJOIN,
  FIELD_NAME_IS_SEARCHABLE,
  FIELD_NAME_ROOM_NAME,
  IsAutojoin,
  IsSearchable,
  RoomName,
} from "../Form";

// Centralized strings for internationalization
const STRINGS = {
  ADD_NEW_ROOM: "Create",
  AUTOJOIN_TOOLTIP: "Toggle this ON to allow individuals to join without needing manual room owner approval",
  CREATE: "Create Room",
  EDIT: "Edit Room",
  HOST_ORGANIZATION: "Host Organization",
  REQUIRED_FIELD: "* Required Field",
  SEARCHABLE_TOOLTIP:
    'Toggle this ON to allow the room to be publicly searchable in the site search. This means that it would show up in search results in the "Search Rooms" section of the site',
  UPDATE: "Update Room",
} as const;

export type BubbleSelectItem = SelectItem;

interface FormValues {
  [FIELD_NAME_BUBBLE_SELECT]: string;
  [FIELD_NAME_DESCRIPTION]: TextFieldValue;
  [FIELD_NAME_IS_AUTOJOIN]: SwitchValue;
  [FIELD_NAME_IS_SEARCHABLE]: SwitchValue;
  [FIELD_NAME_ROOM_NAME]: TextFieldValue;
}

interface RoomFormModalProps {
  mode: "create" | "edit";
  onSubmit: (values: {
    bubbleId: string;
    description: string;
    isPublic: boolean;
    isSearchable: boolean;
    name: string;
  }) => void;
  bubbleSelectItems?: BubbleSelectProps["selectItems"];

  initialValues?: {
    bubbleId?: string;
    description?: string;
    isPublic?: boolean;
    isSearchable?: boolean;
    name?: string;
  };
  buttonProps?: {
    fullWidth?: boolean;
    variant?: "text" | "outlined" | "contained" | "page-header";
  };
}

const requiredFields: string[] = [FIELD_NAME_BUBBLE_SELECT, FIELD_NAME_DESCRIPTION, FIELD_NAME_ROOM_NAME];

const FormContent = ({
  showBubbleSelect,
  bubbleSelectItems,
}: {
  showBubbleSelect: boolean;
  bubbleSelectItems: BubbleSelectProps["selectItems"];
}) => {
  return (
    <>
      <Grid alignItems="center" container direction="row" spacing={5} size={12}>
        <Tooltip placement="top" title={STRINGS.AUTOJOIN_TOOLTIP}>
          <Grid
            container
            justifyContent="center"
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <IsAutojoin useDefault={false} />
          </Grid>
        </Tooltip>
        <Tooltip placement="top" title={STRINGS.SEARCHABLE_TOOLTIP}>
          <Grid
            container
            justifyContent="center"
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <IsSearchable useDefault={false} />
          </Grid>
        </Tooltip>
      </Grid>
      <Grid container marginBottom="2rem" spacing={3}>
        {showBubbleSelect && (
          <Grid size={12}>
            <BubbleSelect label={STRINGS.HOST_ORGANIZATION} required selectItems={bubbleSelectItems} />
          </Grid>
        )}
        <Grid size={12}>
          <RoomName />
        </Grid>
        <Grid size={12}>
          <Description required textFieldProps={{ multiline: true, rows: 5 }} />
        </Grid>
      </Grid>
      <Box sx={{ mb: 2 }}>
        <Stack spacing={1}>
          <Box>{STRINGS.REQUIRED_FIELD}</Box>
        </Stack>
      </Box>
    </>
  );
};

export const RoomFormModal = ({
  mode,
  onSubmit,
  bubbleSelectItems = [],

  initialValues,
  buttonProps = { fullWidth: true, variant: "contained" },
}: RoomFormModalProps) => {
  const [open, setOpen] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    console.log("Form values on submit:", values);
    if (!values.bubbleSelect) {
      return { [FORM_ERROR]: "organization is required" };
    }

    if (!values.roomName) {
      return { [FORM_ERROR]: "name is required" };
    }

    if (!values.description) {
      return { [FORM_ERROR]: "description is required" };
    }

    // Parse the bubbleSelect value which comes as a stringified object
    let bubbleId: string;
    try {
      const bubbleSelect = JSON.parse(values.bubbleSelect);
      bubbleId = bubbleSelect.id;
    } catch (error) {
      console.error("Failed to parse bubbleSelect:", error);
      return { [FORM_ERROR]: "Invalid organization selected" };
    }

    // Ensure bubbleId is a valid UUID
    if (!bubbleId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bubbleId)) {
      console.log("Invalid UUID:", bubbleId);
      return { [FORM_ERROR]: "Invalid organization selected" };
    }

    // Convert switch values from arrays to booleans by checking if they contain their respective field names
    const isPublic = Array.isArray(values.isAutojoin) && values.isAutojoin.includes(FIELD_NAME_IS_AUTOJOIN);
    const isSearchable = Array.isArray(values.isSearchable) && values.isSearchable.includes(FIELD_NAME_IS_SEARCHABLE);

    console.log("Converted values:", { isPublic, isSearchable });

    onSubmit({
      bubbleId,
      description: values.description,
      isPublic,
      isSearchable,
      name: values.roomName,
    });

    setOpen(false);
  };

  const getInitialValues = () => {
    if (!initialValues) {
      return undefined;
    }

    const formValues = {
      [FIELD_NAME_BUBBLE_SELECT]: initialValues.bubbleId
        ? JSON.stringify(
            bubbleSelectItems?.find((item) => item.id === initialValues.bubbleId) ?? {
              id: initialValues.bubbleId,
              value: "Unknown Organization",
            },
          )
        : undefined,
      [FIELD_NAME_DESCRIPTION]: initialValues.description,
      [FIELD_NAME_IS_AUTOJOIN]: initialValues.isPublic ? ["isAutojoin"] : [],
      [FIELD_NAME_IS_SEARCHABLE]: initialValues.isSearchable ? ["isSearchable"] : [],
      [FIELD_NAME_ROOM_NAME]: initialValues.name,
    };
    return formValues;
  };

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {mode === "create" ? (
        <Button
          variant={buttonProps?.variant ?? "contained"}
          onClick={handleOpen}
          aria-label={STRINGS.ADD_NEW_ROOM}
          fullWidth={buttonProps?.fullWidth}
          startIcon={<AddIcon />}
          sx={{ borderRadius: "12px", fontWeight: "bold", textTransform: "none" }}
        >
          {STRINGS.ADD_NEW_ROOM}
        </Button>
      ) : (
        <Tooltip title={STRINGS.EDIT}>
          <IconButton
            onClick={handleOpen}
            aria-label="Edit room"
            size="small"
            sx={{
              "&:hover": { backgroundColor: "action.selected" },
              backgroundColor: "action.hover",
            }}
            data-testid="edit-room-button"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <MuiDialog fullWidth maxWidth="md" onClose={handleClose} open={open}>
        <DialogTitle>
          {mode === "create" ? STRINGS.ADD_NEW_ROOM : STRINGS.EDIT}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Form<FormValues> onSubmit={handleSubmit} initialValues={getInitialValues()}>
          {({ handleSubmit: onFormSubmit }) => (
            <>
              <DialogContent>
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    console.log("Form submitted, calling onFormSubmit");
                    onFormSubmit(e);
                  }}
                  display="grid"
                  gap="2rem"
                  gridAutoColumns="minmax(0, 1fr)"
                  gridAutoFlow="row"
                  sx={{ pt: 2 }}
                >
                  <FormContent bubbleSelectItems={bubbleSelectItems} showBubbleSelect={true} />
                </Box>
              </DialogContent>
              <DialogActions>
                <SubmitButton
                  type="submit"
                  aria-label={mode === "create" ? "Create room" : "Update room"}
                  requiredFields={requiredFields}
                  onClick={(e) => {
                    e.preventDefault();
                    console.log("Submit button clicked, submitting form");
                    onFormSubmit(e);
                  }}
                >
                  {mode === "create" ? STRINGS.CREATE : STRINGS.UPDATE}
                </SubmitButton>
              </DialogActions>
            </>
          )}
        </Form>
      </MuiDialog>
    </>
  );
};
